import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    getGlobalRefactoringReasonsAPI,
    getOrganizationRefactoringReasonsAPI,
    postOrganizationRefactoringReasonAPI,
    putOrganizationRefactoringReasonAPI
} from '../store/user/restCallUser';
import { OrganizationRefactoringReason } from '../store/user/types';

interface Props { organizationId: string; }
interface GlobalReason { id: string; code: string; description: string; allowedMethod: string; status: string; }
const dataOf = (response: any) => { const value = response?.Response?.data || response?.Response; try { return typeof value === 'string' ? JSON.parse(value) : value; } catch { return value; } };

export default function OrganizationRefactoringReasonsManagement({ organizationId }: Props) {
    const [form] = Form.useForm();
    const [rows, setRows] = useState<OrganizationRefactoringReason[]>([]);
    const [catalogue, setCatalogue] = useState<GlobalReason[]>([]);
    const [editing, setEditing] = useState<OrganizationRefactoringReason | null>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [local, global] = await Promise.all([getOrganizationRefactoringReasonsAPI(organizationId), getGlobalRefactoringReasonsAPI()]);
            setRows(Array.isArray(dataOf(local)) ? dataOf(local) : []);
            setCatalogue((Array.isArray(dataOf(global)) ? dataOf(global) : []).filter((x: GlobalReason) => x.status === 'ACTIVO'));
        } catch (e: any) { message.error(e?.message || 'No se pudieron cargar los motivos.'); }
        finally { setLoading(false); }
    }, [organizationId]);
    useEffect(() => { void load(); }, [load]);

    const columns = useMemo<ColumnsType<OrganizationRefactoringReason>>(() => [
        { title: 'Motivo', render: (_, x) => x.customDescription || x.description },
        { title: 'Método permitido', dataIndex: 'allowedMethod' },
        { title: 'Exige observación', render: (_, x) => x.requiresObservation ? 'Sí' : 'No' },
        { title: 'Estado', render: (_, x) => <Tag color={x.status === 'ACTIVO' ? 'green' : 'default'}>{x.status}</Tag> },
        { title: 'Acciones', render: (_, x) => <Button size="small" onClick={() => { setEditing(x); form.setFieldsValue({ documentRefactoringReasonId: x.documentRefactoringReasonId, customDescription: x.customDescription, requiresObservation: x.requiresObservation, status: x.status }); setOpen(true); }}>Editar</Button> }
    ], [form]);

    const save = async () => {
        const values = await form.validateFields();
        setLoading(true);
        try {
            const response = editing
                ? await putOrganizationRefactoringReasonAPI(organizationId, editing.id, values)
                : await postOrganizationRefactoringReasonAPI(organizationId, values);
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudo guardar.');
            setOpen(false); setEditing(null); form.resetFields(); await load(); message.success('Motivo guardado.');
        } catch (e: any) { message.error(e?.message || 'No se pudo guardar.'); }
        finally { setLoading(false); }
    };

    return <>
        <Alert type="info" showIcon message="Motivos controlados por la organización" description="El administrador habilita motivos del catálogo global. Los facturadores solo podrán seleccionar estos motivos." style={{ marginBottom: 16 }} />
        <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}><Button type="primary" onClick={() => { setEditing(null); form.setFieldsValue({ requiresObservation: false, status: 'ACTIVO' }); setOpen(true); }}>Habilitar motivo</Button></Space>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={false} />
        <Modal open={open} title={editing ? 'Editar motivo' : 'Habilitar motivo'} onCancel={() => setOpen(false)} onOk={() => void save()} confirmLoading={loading}>
            <Form form={form} layout="vertical">
                <Form.Item name="documentRefactoringReasonId" label="Motivo global" rules={[{ required: true }]}>
                    <Select disabled={Boolean(editing)} options={catalogue.map(x => ({ value: x.id, label: `${x.description} (${x.allowedMethod})` }))} />
                </Form.Item>
                <Form.Item name="customDescription" label="Descripción visible personalizada"><Input maxLength={255} placeholder="Opcional; si se omite se usa el texto global" /></Form.Item>
                <Form.Item name="requiresObservation" label="Exigir observación" valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="status" label="Estado" rules={[{ required: true }]}><Select options={[{ value: 'ACTIVO' }, { value: 'INACTIVO' }]} /></Form.Item>
            </Form>
        </Modal>
    </>;
}
